import { useEffect, useRef, useState, type CSSProperties } from "react";
import { Link } from "wouter";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { getDemoProfile, type DemoProfileData } from "@/data/demoProfiles";
import QRCodeComponent from "react-qr-code";
import {
  ArrowRight, CheckCircle, MapPin, QrCode, type LucideIcon,
  UtensilsCrossed, Building2, Dumbbell, Scissors, Video, Stethoscope, ShoppingBag, CalendarDays, Briefcase,
  TrendingUp, BedDouble, Coffee, Pill, GraduationCap, Camera, Car, Factory, HeartHandshake,
} from "lucide-react";

// ── Block definitions ──────────────────────────────────────────────────────

interface UseBlock {
  index: number;
  slug: string;
  title: string;
  subtitle: string;
  description: string;
  features: string[];
  plan: "free" | "pro" | "enterprise";
  imageUrl: string;
  bgClass: string;
  accentFrom: string;
  accentTo: string;
}

const PLAN_CTA: Record<UseBlock["plan"], { label: string; href: string; style: string }> = {
  free:       { label: "Try Free",       href: "/signup",  style: "bg-primary text-primary-foreground hover:bg-primary/90" },
  pro:        { label: "Start Pro",      href: "/pricing", style: "bg-violet-600 text-white hover:bg-violet-700" },
  enterprise: { label: "Get Enterprise", href: "/pricing", style: "bg-foreground text-background hover:opacity-90" },
};

const USE_CASES: UseBlock[] = [
  {
    index: 1, slug: "restaurant", plan: "free",
    title: "Restaurant", subtitle: "Table QR · Digital Menu · Reservations",
    description: "Replace paper menus with a dynamic QR profile. Let diners view today's specials, reserve tables, and order online — all from one scan.",
    features: ["Live digital menu, always up to date", "WhatsApp table reservations", "Google Maps & directions link", "Instagram food gallery"],
    imageUrl: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=600&q=80&auto=format&fit=crop",
    bgClass: "bg-rose-50", accentFrom: "from-orange-500", accentTo: "to-red-600",
  },
  {
    index: 2, slug: "real-estate", plan: "pro",
    title: "Real Estate", subtitle: "Property Listings · Site Visits · Agent Profile",
    description: "Agents and agencies can link all listings, photos, and booking options in one scannable profile — perfect on yard signs, brochures, and property portals.",
    features: ["Browse live property listings", "Book site visits via WhatsApp", "Photo gallery & virtual tour link", "Direct agent call & LinkedIn"],
    imageUrl: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=600&q=80&auto=format&fit=crop",
    bgClass: "bg-blue-50", accentFrom: "from-blue-600", accentTo: "to-indigo-800",
  },
  {
    index: 3, slug: "fitness-studio", plan: "pro",
    title: "Fitness Studio", subtitle: "Gym · Membership · Personal Training",
    description: "Print on flyers, gym windows, and ads. Prospects scan to see transformation stories, book a free tour, and sign up — straight from the QR code.",
    features: ["One-tap membership sign-up", "Free consultation via WhatsApp", "YouTube workout library link", "Transformation story gallery"],
    imageUrl: "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=600&q=80&auto=format&fit=crop",
    bgClass: "bg-emerald-50", accentFrom: "from-green-500", accentTo: "to-emerald-700",
  },
  {
    index: 4, slug: "beauty-salon", plan: "free",
    title: "Beauty Salon", subtitle: "Hair · Skin · Nails · Appointments",
    description: "Give clients an instant way to book appointments, browse your work, and share their favourite stylist — from a card, mirror sticker, or reception QR.",
    features: ["Online appointment booking", "Instagram portfolio gallery", "WhatsApp instant booking", "Facebook reviews & page"],
    imageUrl: "https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=600&q=80&auto=format&fit=crop",
    bgClass: "bg-fuchsia-50", accentFrom: "from-pink-500", accentTo: "to-fuchsia-700",
  },
  {
    index: 5, slug: "content-creator", plan: "pro",
    title: "Creator / YouTuber", subtitle: "Social Hub · Collab · Media Kit",
    description: "Your link-in-bio, levelled up. One QR connects fans and brand partners to every platform, collab form, and media kit — scannable on merch, thumbnails, and events.",
    features: ["All social platforms in one place", "Collab enquiry via WhatsApp", "Media kit & rate card link", "2M+ TikTok & YouTube combined"],
    imageUrl: "https://images.unsplash.com/photo-1611532736597-de2d4265fba3?w=600&q=80&auto=format&fit=crop",
    bgClass: "bg-orange-50", accentFrom: "from-fuchsia-500", accentTo: "to-pink-700",
  },
  {
    index: 6, slug: "doctor", plan: "free",
    title: "Doctor / Clinic", subtitle: "Appointments · Patient Portal · Credentials",
    description: "Doctors and clinics can share credentials, booking links, and patient support through a verified QR profile — on prescription pads, waiting rooms, and business cards.",
    features: ["Online consultation booking", "Verified credentials display", "Patient portal quick access", "WhatsApp clinic support"],
    imageUrl: "https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=600&q=80&auto=format&fit=crop",
    bgClass: "bg-teal-50", accentFrom: "from-teal-500", accentTo: "to-cyan-700",
  },
  {
    index: 7, slug: "retail-store", plan: "pro",
    title: "Retail Store", subtitle: "Shop · Style Enquiries · New Arrivals",
    description: "Place a QR at the entrance, fitting room, or checkout. Shoppers instantly reach your online store, new arrivals, or WhatsApp personal shopper.",
    features: ["Direct link to online shop", "WhatsApp style consultant", "Instagram new arrivals feed", "Store locator & hours"],
    imageUrl: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=600&q=80&auto=format&fit=crop",
    bgClass: "bg-muted", accentFrom: "from-slate-700", accentTo: "to-gray-900",
  },
  {
    index: 8, slug: "event-manager", plan: "pro",
    title: "Event Manager", subtitle: "Conference · Schedule · Attendee Support",
    description: "Event QR badges and passes that work. Attendees scan to get the full schedule, venue map, speaker bios, and support — no app download needed.",
    features: ["Live event schedule & updates", "Venue map & directions", "Real-time attendee support", "LinkedIn & sponsor pages"],
    imageUrl: "https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=600&q=80&auto=format&fit=crop",
    bgClass: "bg-indigo-50", accentFrom: "from-indigo-600", accentTo: "to-blue-800",
  },
  {
    index: 9, slug: "freelancer", plan: "free",
    title: "Freelancer", subtitle: "Digital Business Card · Portfolio · Contact",
    description: "The modern business card. Hand one QR to a prospect and they instantly have your LinkedIn, portfolio, phone, email, and WhatsApp — no more typing.",
    features: ["LinkedIn profile direct link", "Portfolio website showcase", "One-tap WhatsApp & call", "Email with subject pre-filled"],
    imageUrl: "https://images.unsplash.com/photo-1497032628192-86f99bcd76bc?w=600&q=80&auto=format&fit=crop",
    bgClass: "bg-violet-50", accentFrom: "from-blue-500", accentTo: "to-indigo-700",
  },
  {
    index: 10, slug: "consultant", plan: "pro",
    title: "Consultant", subtitle: "Executive Coaching · Strategy · Booking",
    description: "High-end consultants and coaches build credibility instantly. Share a QR on proposals, email signatures, or LinkedIn — clients book a session in one tap.",
    features: ["Session booking with one tap", "LinkedIn & credentials display", "Speaking topics & media links", "Email & WhatsApp direct access"],
    imageUrl: "https://images.unsplash.com/photo-1551836022-d5d88e9218df?w=600&q=80&auto=format&fit=crop",
    bgClass: "bg-cyan-50", accentFrom: "from-teal-600", accentTo: "to-emerald-700",
  },
  {
    index: 11, slug: "hotel", plan: "pro",
    title: "Hotel", subtitle: "Bookings · Concierge · Resort Experience",
    description: "From lobby cards to in-room tablets, a hotel QR profile gives guests instant access to bookings, concierge services, dining menus, and local guides.",
    features: ["Direct room booking link", "24/7 concierge via WhatsApp", "Dining & spa reservation links", "Resort gallery & virtual tour"],
    imageUrl: "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=600&q=80&auto=format&fit=crop",
    bgClass: "bg-amber-50", accentFrom: "from-cyan-500", accentTo: "to-blue-700",
  },
  {
    index: 12, slug: "cafe", plan: "free",
    title: "Café / Coffee", subtitle: "Menu · Loyalty · Neighbourhood Discovery",
    description: "Stick a QR on your counter, tables, and takeaway cups. New customers discover you, regulars get loyalty offers, and everyone can find your menu in seconds.",
    features: ["Digital menu always current", "WhatsApp order & pickup", "Google Maps directions", "Instagram daily specials"],
    imageUrl: "https://images.unsplash.com/photo-1501339847302-ac426a4a7cbb?w=600&q=80&auto=format&fit=crop",
    bgClass: "bg-lime-50", accentFrom: "from-amber-600", accentTo: "to-orange-800",
  },
  {
    index: 13, slug: "pharmacy", plan: "free",
    title: "Pharmacy", subtitle: "Prescription Refills · Health Checks · Delivery",
    description: "Community pharmacies can accept prescription refills, book health check appointments, and offer home delivery — all from a single trusted QR profile.",
    features: ["WhatsApp prescription refill", "Health check booking", "Home delivery request", "Emergency helpline direct call"],
    imageUrl: "https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=600&q=80&auto=format&fit=crop",
    bgClass: "bg-sky-50", accentFrom: "from-sky-500", accentTo: "to-blue-700",
  },
  {
    index: 14, slug: "education", plan: "pro",
    title: "Education", subtitle: "Coaching · Admissions · Free Resources",
    description: "Schools, tutors, and coaching institutes use QR profiles on hoardings and brochures to let students enrol, access free lectures, and get counselling instantly.",
    features: ["One-click course enrolment", "Free counselling via WhatsApp", "YouTube lecture library link", "Results & achievements display"],
    imageUrl: "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=600&q=80&auto=format&fit=crop",
    bgClass: "bg-purple-50", accentFrom: "from-purple-600", accentTo: "to-violet-800",
  },
  {
    index: 15, slug: "photographer", plan: "free",
    title: "Photographer", subtitle: "Portfolio · Bookings · Behind the Scenes",
    description: "Print your QR on business cards, photo albums, and packaging. Clients see your full portfolio, check availability, and book sessions without a single email.",
    features: ["Full portfolio website link", "Instagram gallery showcase", "Availability check via WhatsApp", "Behind-the-scenes YouTube"],
    imageUrl: "https://images.unsplash.com/photo-1542038784456-1ea8e935640e?w=600&q=80&auto=format&fit=crop",
    bgClass: "bg-pink-50", accentFrom: "from-pink-600", accentTo: "to-rose-800",
  },
  {
    index: 16, slug: "taxi-transport", plan: "pro",
    title: "Taxi / Transport", subtitle: "Ride Booking · Corporate · Fleet",
    description: "In-vehicle QR codes let passengers book return trips, access corporate packages, and leave reviews — turning every ride into a repeat customer opportunity.",
    features: ["Instant ride booking by call / WhatsApp", "Corporate account packages", "Fleet gallery & vehicle types", "Airport & station pick-up links"],
    imageUrl: "https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?w=600&q=80&auto=format&fit=crop",
    bgClass: "bg-yellow-50", accentFrom: "from-yellow-500", accentTo: "to-amber-700",
  },
  {
    index: 17, slug: "manufacturer", plan: "enterprise",
    title: "Manufacturer", subtitle: "B2B · Supply Chain · Corporate Identity",
    description: "Manufacturers print QR profiles on letterheads, product packaging, and exhibition stands — giving partners, distributors, and clients one authoritative digital touchpoint.",
    features: ["Corporate website & portal", "B2B enquiry email direct", "LinkedIn company profile", "Head office map & switchboard"],
    imageUrl: "https://images.unsplash.com/photo-1565043589221-1a6fd9ae45c7?w=600&q=80&auto=format&fit=crop",
    bgClass: "bg-gray-100", accentFrom: "from-indigo-800", accentTo: "to-slate-900",
  },
  {
    index: 18, slug: "charity-ngo", plan: "free",
    title: "Charity / NGO", subtitle: "Donations · Volunteers · Impact Stories",
    description: "Non-profits and NGOs use QR profiles on donation boxes, event banners, and mailers to make it effortless to donate, volunteer, and share the mission.",
    features: ["One-tap donation link", "Volunteer sign-up form", "Impact story YouTube & Instagram", "Direct contact for partnerships"],
    imageUrl: "https://images.unsplash.com/photo-1532629345422-7515f3d16bb6?w=600&q=80&auto=format&fit=crop",
    bgClass: "bg-green-50", accentFrom: "from-green-600", accentTo: "to-emerald-800",
  },
];

// ── Sub-components ─────────────────────────────────────────────────────────

const PLATFORM_ICON: Record<string, string> = {
  whatsapp: "💬", phone: "📞", email: "✉️", location: "📍", website: "🌐",
  instagram: "📸", facebook: "👍", linkedin: "💼", youtube: "▶️", tiktok: "🎵",
  twitter: "𝕏", payment: "💳", telegram: "✈️",
};

// Live theme from the CommonQR theme library for each use case (theme_id, served by /api/v1/themes/image/:themeId).
const DEMO_THEME: Record<string, string> = {
  "restaurant":      "fine-dining-gastronomy-michelin-star-restaurant-bold-1789895591280",
  "real-estate":     "real-estate---property-luxury-villa-bold",
  "fitness-studio":  "fitness-gym-fitness-center-bold",
  "beauty-salon":    "beauty-hair-salon-bold",
  "content-creator": "creative-media-content-creator-bold",
  "doctor":          "healthcare-general-doctor-bold",
  "retail-store":    "fashion-apparel-boutique-clothing-bold",
  "event-manager":   "events-entertainment-corporate-events-bold",
  "freelancer":      "personal-personal-brand-bold",
  "consultant":      "business-consulting-strategy-consultant-bold-1789840425464",
  "hotel":           "hotels-luxury-hotel-bold",
  "cafe":            "coffee-caf-culture-specialty-coffee-shop-bold-1789895590974",
  "pharmacy":        "pharmaceutical-retail-pharmacy-pharmacist-bold-1790343075593",
  "education":       "education-school-tuition-center-bold",
  "photographer":    "photography-videography-wedding-photographer-bold-1789896040767",
  "taxi-transport":  "topup-transport-travel-night-drive-city-bold",
  "manufacturer":    "manufacturing-industry-electronics-manufacturing-bold",
  "charity-ngo":     "social-impact-ngo-ngo-team-bold-1790316818515",
};

// Portrait crop of the use-case photo — second layer, used only if the theme image fails.
function portraitUrl(url: string) {
  return url.includes("h=") ? url : url.replace("w=600", "w=600&h=900");
}

// Preview card — same visual language as the home page hero cards:
// themed photo, dark fade, glass avatar and glass link buttons, no device chrome.
function MiniPhone({ demo, block }: { demo: DemoProfileData; block: UseBlock }) {
  const themeId = DEMO_THEME[block.slug];
  const layers = [
    themeId ? `url("/api/v1/themes/image/${encodeURIComponent(themeId)}")` : null,
    `url("${portraitUrl(block.imageUrl)}")`,
  ].filter(Boolean).join(", ");
  return (
    <Link href={`/demos/${demo.slug}`} className="block group">
      <div className={`relative h-[480px] w-[240px] shrink-0 overflow-hidden rounded-[40px] bg-gradient-to-br ${block.accentFrom} ${block.accentTo} shadow-[0_8px_32px_rgba(0,0,0,0.3)] transition-transform group-hover:-translate-y-1`}>
        {/* Layers: live theme image, then the use-case photo, then the accent gradient — each covers a failure of the one above */}
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: layers }} />
        <div className="absolute inset-0 bg-[linear-gradient(to_bottom,rgba(0,0,0,0.1)_0%,rgba(0,0,0,0.5)_30%,rgba(0,0,0,0.85)_100%)]" />
        <span className="absolute right-4 top-4 z-10 rounded-full bg-amber-400 px-2 py-0.5 text-[9px] font-bold tracking-wider text-amber-950">DEMO</span>
        <div className="absolute inset-x-0 top-[16%] z-10 px-4">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border-2 border-white bg-white/20 text-lg font-bold text-white backdrop-blur-sm">
            {demo.avatarInitial}
          </div>
          <h3 className="mt-2 truncate text-center text-base font-bold text-white [text-shadow:0_1px_4px_rgba(0,0,0,0.6)]">{demo.name}</h3>
          <p className="mt-0.5 flex items-center justify-center gap-1 truncate text-[11px] text-white/70">
            <MapPin className="h-3 w-3 shrink-0" /> {demo.location}
          </p>
          <p className="mt-2 line-clamp-2 text-center text-[11px] leading-snug text-white/80">{demo.bio}</p>
          <div className="mt-3 flex flex-col gap-1.5">
            {demo.links.slice(0, 4).map((link) => (
              <div
                key={link.sortOrder}
                className="flex h-[34px] w-full items-center justify-center rounded-full border border-white/25 bg-white/[0.18] px-3 text-xs font-semibold text-white backdrop-blur-[8px]"
              >
                <span className="mr-1.5 shrink-0" aria-hidden="true">{PLATFORM_ICON[link.platform] ?? "🔗"}</span>
                <span className="truncate">{link.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Link>
  );
}

function DemoQRCard({ demo, block }: { demo: DemoProfileData; block: UseBlock }) {
  const profileUrl = `https://commonqr.com/demos/${demo.slug}`;
  return (
    <Link href={`/demos/${demo.slug}`} className="block group">
      <div className="bg-card rounded-3xl shadow-xl shadow-black/10 border border-border/80 p-6 flex flex-col items-center gap-4 hover:shadow-2xl hover:-translate-y-1 transition-all">
        {/* DEMO badge */}
        <span className="text-[10px] font-bold bg-amber-100 text-amber-700 px-3 py-1 rounded-full tracking-widest uppercase">
          DEMO
        </span>
        {/* QR */}
        <div className={`bg-gradient-to-br ${block.accentFrom} ${block.accentTo} p-[3px] rounded-2xl`}>
          <div className="bg-card rounded-[14px] p-3">
            <QRCodeComponent value={profileUrl} size={110} level="M" fgColor="#0f172a" bgColor="#ffffff" />
          </div>
        </div>
        {/* Profile name */}
        <div className="text-center">
          <p className="font-bold text-sm">{demo.name}</p>
          <p className="text-[11px] text-muted-foreground">@{demo.username}</p>
        </div>
        {/* Scan count */}
        <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground bg-muted/50 px-3 py-1.5 rounded-full">
          <QrCode className="w-3 h-3" />
          <span><span className="font-bold text-foreground">{demo.totalScans.toLocaleString()}</span> scans</span>
        </div>
        {/* CTA */}
        <span className="text-[11px] font-semibold text-primary flex items-center gap-1 group-hover:gap-2 transition-all">
          View Full Demo <ArrowRight className="w-3.5 h-3.5" />
        </span>
      </div>
    </Link>
  );
}

function DetailsPanel({ block, imageUrl }: { block: UseBlock; imageUrl: string }) {
  const cta = PLAN_CTA[block.plan];
  return (
    <div className="flex flex-col gap-5">
      {/* Stock image */}
      <div className={`rounded-2xl overflow-hidden aspect-video w-full shadow-md bg-gradient-to-br ${block.accentFrom} ${block.accentTo}`}>
        <img
          src={imageUrl}
          alt={block.title}
          className="w-full h-full object-cover"
          loading="lazy"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
      </div>
      {/* Plan badge */}
      <div>
        <span className={`text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full ${
          block.plan === "free" ? "bg-emerald-100 text-emerald-700"
          : block.plan === "pro" ? "bg-violet-100 text-violet-700"
          : "bg-border text-foreground/80"
        }`}>
          {block.plan === "free" ? "Free Plan" : block.plan === "pro" ? "Pro Plan" : "Enterprise"}
        </span>
      </div>
      {/* Title */}
      <div>
        <h2 className="text-3xl font-display font-bold leading-tight text-foreground">{block.title}</h2>
        <p className="text-sm font-medium text-muted-foreground mt-1">{block.subtitle}</p>
      </div>
      {/* Description */}
      <p className="text-sm text-muted-foreground leading-relaxed">{block.description}</p>
      {/* Features */}
      <ul className="space-y-2">
        {block.features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm text-foreground">
            <CheckCircle className="w-4 h-4 text-primary shrink-0 mt-0.5" />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      {/* CTA */}
      <div className="flex items-center gap-3 flex-wrap">
        <Link
          href={cta.href}
          className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-sm shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all ${cta.style}`}
        >
          {cta.label} <ArrowRight className="w-4 h-4" />
        </Link>
        <Link
          href={`/demos/${block.slug}`}
          className="text-sm text-primary font-semibold underline-offset-2 hover:underline"
        >
          View live demo →
        </Link>
      </div>
    </div>
  );
}

// ── Industry labels ────────────────────────────────────────────────────────
// Shown above each use case and in the side navigation. Colours: [light mode, dark mode].

const INDUSTRY: Record<string, { label: string; Icon: LucideIcon; color: [string, string] }> = {
  "restaurant":      { label: "Food & Dining",          Icon: UtensilsCrossed, color: ["#ea580c", "#fb923c"] },
  "real-estate":     { label: "Real Estate",            Icon: Building2,       color: ["#2563eb", "#60a5fa"] },
  "fitness-studio":  { label: "Fitness & Sports",       Icon: Dumbbell,        color: ["#16a34a", "#4ade80"] },
  "beauty-salon":    { label: "Beauty & Wellness",      Icon: Scissors,        color: ["#db2777", "#f472b6"] },
  "content-creator": { label: "Creators & Media",       Icon: Video,           color: ["#c026d3", "#e879f9"] },
  "doctor":          { label: "Healthcare",             Icon: Stethoscope,     color: ["#0d9488", "#2dd4bf"] },
  "retail-store":    { label: "Retail & Fashion",       Icon: ShoppingBag,     color: ["#475569", "#94a3b8"] },
  "event-manager":   { label: "Events",                 Icon: CalendarDays,    color: ["#4f46e5", "#818cf8"] },
  "freelancer":      { label: "Freelancers",            Icon: Briefcase,       color: ["#2563eb", "#60a5fa"] },
  "consultant":      { label: "Consulting",             Icon: TrendingUp,      color: ["#0d9488", "#2dd4bf"] },
  "hotel":           { label: "Hotels & Hospitality",   Icon: BedDouble,       color: ["#0891b2", "#22d3ee"] },
  "cafe":            { label: "Cafés & Coffee",         Icon: Coffee,          color: ["#d97706", "#fbbf24"] },
  "pharmacy":        { label: "Pharmacy",               Icon: Pill,            color: ["#0284c7", "#38bdf8"] },
  "education":       { label: "Education",              Icon: GraduationCap,   color: ["#9333ea", "#c084fc"] },
  "photographer":    { label: "Photography",            Icon: Camera,          color: ["#e11d48", "#fb7185"] },
  "taxi-transport":  { label: "Transport & Travel",     Icon: Car,             color: ["#ca8a04", "#facc15"] },
  "manufacturer":    { label: "Manufacturing",          Icon: Factory,         color: ["#4f46e5", "#818cf8"] },
  "charity-ngo":     { label: "Non-profit & NGO",       Icon: HeartHandshake,  color: ["#16a34a", "#4ade80"] },
};

// Industry colour + glass CTA card. Plain CSS so it follows the site's light/dark class.
const PAGE_CSS = `
.cqr-ind { color: var(--ind-l); }
.dark .cqr-ind { color: var(--ind-d); }
.cqr-cta {
  --cta-bg: rgba(255,255,255,0.55); --cta-border: rgba(15,23,42,0.10);
  --cta-shadow: 0 20px 50px -20px rgba(15,23,42,0.25); --cta-sheen: rgba(255,255,255,0.7);
  position: relative; overflow: hidden; background: var(--cta-bg); border: 1px solid var(--cta-border);
  box-shadow: var(--cta-shadow); backdrop-filter: blur(22px) saturate(180%); -webkit-backdrop-filter: blur(22px) saturate(180%);
}
.dark .cqr-cta {
  --cta-bg: rgba(255,255,255,0.05); --cta-border: rgba(255,255,255,0.14);
  --cta-shadow: 0 20px 50px -20px rgba(0,0,0,0.8); --cta-sheen: rgba(255,255,255,0.12);
}
.cqr-cta::before {
  content: ""; position: absolute; inset: 0 0 auto 0; height: 1px; pointer-events: none;
  background: linear-gradient(90deg, transparent, var(--cta-sheen), transparent);
}
`;

function IndustryLabel({ slug, className = "" }: { slug: string; className?: string }) {
  const ind = INDUSTRY[slug];
  if (!ind) return null;
  const { Icon } = ind;
  return (
    <span
      className={`cqr-ind inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest ${className}`}
      style={{ "--ind-l": ind.color[0], "--ind-d": ind.color[1] } as CSSProperties}
    >
      <Icon className="h-4 w-4 shrink-0" aria-hidden="true" /> {ind.label}
    </span>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────

export default function DemoProfiles() {
  const [active, setActive] = useState(1);
  const pillsRef = useRef<HTMLDivElement>(null);

  // Highlight the use case currently in the middle of the screen.
  useEffect(() => {
    const sections = USE_CASES.map((b) => document.getElementById(`block-${b.index}`)).filter(Boolean) as HTMLElement[];
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) setActive(Number(e.target.id.replace("block-", "")));
        }
      },
      { rootMargin: "-45% 0px -50% 0px" },
    );
    sections.forEach((s) => io.observe(s));
    return () => io.disconnect();
  }, []);

  // Keep the active pill visible in the mobile row (horizontal scroll only).
  useEffect(() => {
    const row = pillsRef.current;
    const pill = row?.querySelector<HTMLElement>(`[data-index="${active}"]`);
    if (row && pill) row.scrollTo({ left: pill.offsetLeft - row.clientWidth / 2 + pill.clientWidth / 2, behavior: "smooth" });
  }, [active]);

  return (
    <div className="min-h-screen bg-background">
      <style>{PAGE_CSS}</style>
      <Navbar />

      <div className="lg:flex">
        {/* ── Side navigation (desktop) ── */}
        <aside className="hidden w-[220px] shrink-0 border-r border-border bg-card lg:block">
          <nav className="sticky top-[57px] max-h-[calc(100vh-57px)] overflow-y-auto px-3 py-6" aria-label="Industries">
            <p className="mb-2 px-2 text-[9px] font-semibold uppercase tracking-widest text-muted-foreground">Browse by industry</p>
            <div className="flex flex-col gap-0.5">
              {USE_CASES.map((b) => {
                const on = active === b.index;
                return (
                  <a
                    key={b.slug}
                    href={`#block-${b.index}`}
                    aria-current={on ? "true" : undefined}
                    className={`w-full rounded-lg px-2.5 py-1.5 text-left text-sm transition-colors ${on ? "bg-primary font-bold text-primary-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground"}`}
                  >
                    {INDUSTRY[b.slug]?.label ?? b.title}
                  </a>
                );
              })}
            </div>
          </nav>
        </aside>

        <main className="min-w-0 flex-1">
          {/* ── Header ── */}
          <div className="bg-card/50 px-4 pt-8 pb-4 text-center sm:px-6">
            <h1 className="flex items-center justify-center gap-3 text-2xl font-extrabold tracking-tight text-foreground sm:text-3xl">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/15 text-primary">
                <QrCode className="h-5 w-5" />
              </span>
              Live Demo Profiles
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">Real-world examples of CommonQR in action. Scan any demo to try it live.</p>
          </div>

          {/* ── Industry pills (mobile) ── */}
          <div className="sticky top-[57px] z-20 border-b border-border/50 bg-background/90 backdrop-blur lg:hidden">
            <div ref={pillsRef} className="overflow-x-auto px-4 py-3 [scrollbar-width:none]">
              <div className="flex min-w-max gap-2">
                {USE_CASES.map((b) => {
                  const on = active === b.index;
                  return (
                    <a
                      key={b.slug}
                      href={`#block-${b.index}`}
                      data-index={b.index}
                      className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${on ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-muted-foreground hover:border-primary/60 hover:text-foreground"}`}
                    >
                      {INDUSTRY[b.slug]?.label ?? b.title}
                    </a>
                  );
                })}
              </div>
            </div>
          </div>

          {/* ── Use cases ── */}
          <div id="blocks">
            {USE_CASES.map((block) => {
              const demo = getDemoProfile(block.slug);
              if (!demo) return null;
              const reversed = block.index % 2 === 0;

              return (
                <section
                  key={block.slug}
                  id={`block-${block.index}`}
                  className="relative overflow-hidden bg-background border-t border-border/50 py-20 scroll-mt-[121px] lg:scroll-mt-[57px]"
                >
                  <div aria-hidden="true" className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${block.accentFrom} ${block.accentTo} opacity-[0.07]`} />
                  <div className="relative max-w-7xl mx-auto px-6">
                    {/* Industry label */}
                    <div className="mb-10">
                      <IndustryLabel slug={block.slug} />
                    </div>

                    {/* 3-column layout — stacks on mobile, alternates on desktop */}
                    <div className={`grid grid-cols-1 lg:grid-cols-3 gap-10 items-center ${reversed ? "lg:[&>*:first-child]:order-3 lg:[&>*:last-child]:order-1" : ""}`}>
                      {/* Col 1: Details */}
                      <DetailsPanel block={block} imageUrl={block.imageUrl} />

                      {/* Col 2: QR card (always centre) */}
                      <div className="flex justify-center">
                        <DemoQRCard demo={demo} block={block} />
                      </div>

                      {/* Col 3: Phone preview */}
                      <div className="flex justify-center">
                        <MiniPhone demo={demo} block={block} />
                      </div>
                    </div>
                  </div>
                </section>
              );
            })}
          </div>

          {/* ── More industries ── */}
          <div className="border-t border-border/50 px-6 pt-14 text-center">
            <p className="text-sm text-muted-foreground sm:text-base">
              Don't see your industry? CommonQR has themes for <strong className="font-bold text-foreground">216 industries</strong> —{" "}
              <Link href="/themes" className="whitespace-nowrap font-semibold text-primary underline-offset-2 hover:underline">
                browse all themes →
              </Link>
            </p>
          </div>

          {/* ── Bottom CTA ── */}
          <div className="px-4 py-14 sm:py-16">
            <div className="cqr-cta mx-auto max-w-xl rounded-3xl px-6 py-10 text-center sm:px-10">
              <h3 className="text-3xl font-display font-bold text-foreground">Ready to build yours?</h3>
              <p className="mt-3 text-muted-foreground">
                Create your CommonQR profile in 60 seconds. Free plan included. No credit card needed.
              </p>
              <div className="mt-7 flex flex-wrap justify-center gap-3">
                <Link
                  href="/signup"
                  className="inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-3 text-sm font-bold text-primary-foreground shadow-sm transition-all hover:-translate-y-0.5 hover:bg-primary/90 hover:shadow-md"
                >
                  Get Started Free <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  href="/pricing"
                  className="inline-flex items-center gap-2 rounded-xl border border-border bg-background/40 px-6 py-3 text-sm font-semibold text-foreground transition-all hover:border-primary/60"
                >
                  View Pricing
                </Link>
              </div>
            </div>
          </div>
        </main>
      </div>

      <Footer />
    </div>
  );
}
